import {
  classifyRecognitionError,
  detectVoiceRuntime,
} from "./voiceInputRuntime";

// Framework-independent EDEN voice-input state machine. Keep this module
// portable: React owns one controller instance, while the controller owns the
// browser recognition object, microphone stream, timers, and session
// generation.

export const VOICE_INPUT_PHASES = Object.freeze({
  IDLE: "idle",
  REQUESTING_PERMISSION: "requesting",
  LISTENING: "listening",
  PROCESSING: "processing",
  CAPTURED: "captured",
  NO_SPEECH: "no-speech",
  PERMISSION_DENIED: "permission-denied",
  RECOGNITION_UNAVAILABLE: "unavailable",
  AUDIO_CAPTURE_FAILURE: "audio-failure",
  ERROR: "error",
});

export const VOICE_INPUT_STATUS_MESSAGES = Object.freeze({
  [VOICE_INPUT_PHASES.IDLE]:
    "VOICE INPUT STOPPED. TAP TALK WHEN YOU’RE READY.",
  [VOICE_INPUT_PHASES.REQUESTING_PERMISSION]:
    "REQUESTING MICROPHONE ACCESS…",
  [VOICE_INPUT_PHASES.LISTENING]:
    "LISTENING… SPEAK NOW.",
  [VOICE_INPUT_PHASES.PROCESSING]:
    "PROCESSING VOICE COMMAND…",
  [VOICE_INPUT_PHASES.CAPTURED]:
    "VOICE COMMAND SUBMITTED.",
  [VOICE_INPUT_PHASES.NO_SPEECH]:
    "NO SPEECH DETECTED. TAP TALK TO TRY AGAIN.",
  [VOICE_INPUT_PHASES.PERMISSION_DENIED]:
    "MICROPHONE ACCESS IS BLOCKED. ENABLE IT IN YOUR BROWSER OR DEVICE SETTINGS, THEN TAP TALK AGAIN. YOU CAN STILL TYPE.",
  [VOICE_INPUT_PHASES.RECOGNITION_UNAVAILABLE]:
    "SPEECH RECOGNITION IS UNAVAILABLE IN THIS RUNTIME. YOU CAN STILL TYPE TO EDEN.",
  [VOICE_INPUT_PHASES.AUDIO_CAPTURE_FAILURE]:
    "AUDIO CAPTURE FAILED. CHECK YOUR MICROPHONE AND TRY AGAIN.",
  [VOICE_INPUT_PHASES.ERROR]:
    "VOICE INPUT COULD NOT COMPLETE. TAP TALK TO TRY AGAIN, OR CONTINUE BY TYPING.",
});

export const INITIAL_VOICE_INPUT_SNAPSHOT = Object.freeze({
  phase: VOICE_INPUT_PHASES.IDLE,
  message: VOICE_INPUT_STATUS_MESSAGES[VOICE_INPUT_PHASES.IDLE],
  errorCode: null,
  sessionId: null,
  active: false,
  isActive: false,
  isListening: false,
  transcriptChars: 0,
});

export const VOICE_INPUT_DIAGNOSTIC_EVENTS = Object.freeze({
  TALK_GESTURE_RECEIVED: "talk_gesture_received",
  RUNTIME_CAPABILITY_RESULT: "runtime_capability_result",
  MICROPHONE_PERMISSION_REQUESTED: "microphone_permission_requested",
  MEDIA_STREAM_ACQUIRED: "media_stream_acquired",
  RECOGNITION_START_REQUESTED: "recognition_start_requested",
  RECOGNITION_START: "recognition_start",
  RECOGNITION_AUDIOSTART: "recognition_audiostart",
  RECOGNITION_AUDIOEND: "recognition_audioend",
  RECOGNITION_SPEECHSTART: "recognition_speechstart",
  RECOGNITION_SPEECHEND: "recognition_speechend",
  RECOGNITION_RESULT: "recognition_result",
  RECOGNITION_NOMATCH: "recognition_nomatch",
  RECOGNITION_ERROR: "recognition_error",
  RECOGNITION_END: "recognition_end",
  PAGE_INTERRUPTION: "page_interruption",
  USER_STOP: "user_stop",
  COMPONENT_CLEANUP: "component_cleanup",
});

const ACTIVE_PHASES = new Set([
  VOICE_INPUT_PHASES.REQUESTING_PERMISSION,
  VOICE_INPUT_PHASES.LISTENING,
  VOICE_INPUT_PHASES.PROCESSING,
]);

const normalizeTranscript = (value) =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim();

const comparableToken = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9']/g, "");

/**
 * Append a new recognition fragment without duplicating cumulative Web Speech
 * results. The incoming fragment wins when it is a longer cumulative version;
 * otherwise the longest suffix/prefix token overlap is merged.
 */
export function appendVoiceTranscript(existingText, incomingText) {
  const existing = normalizeTranscript(existingText);
  const incoming = normalizeTranscript(incomingText);
  if (!existing) return incoming;
  if (!incoming) return existing;

  const existingLower = existing.toLowerCase();
  const incomingLower = incoming.toLowerCase();
  if (existingLower === incomingLower) return existing;
  if (incomingLower.startsWith(existingLower)) return incoming;
  if (existingLower.endsWith(incomingLower)) return existing;

  const existingTokens = existing.split(" ");
  const incomingTokens = incoming.split(" ");
  const maximumOverlap = Math.min(existingTokens.length, incomingTokens.length);

  for (let overlap = maximumOverlap; overlap > 0; overlap -= 1) {
    const existingSuffix = existingTokens
      .slice(existingTokens.length - overlap)
      .map(comparableToken);
    const incomingPrefix = incomingTokens.slice(0, overlap).map(comparableToken);
    if (
      existingSuffix.every(
        (token, index) => token && token === incomingPrefix[index]
      )
    ) {
      return [...existingTokens, ...incomingTokens.slice(overlap)].join(" ");
    }
  }

  return `${existing} ${incoming}`;
}

export const mergeVoiceTranscript = appendVoiceTranscript;

const safeCall = (callback, ...args) => {
  if (typeof callback !== "function") return undefined;
  try {
    return callback(...args);
  } catch {
    return undefined;
  }
};

const stopMediaStream = (stream) => {
  if (!stream || typeof stream.getTracks !== "function") return 0;
  const tracks = Array.from(stream.getTracks() || []);
  tracks.forEach((track) => {
    try {
      track?.stop?.();
    } catch {}
  });
  return tracks.length;
};

const detectRuntimeProfile = (win, nav) => {
  const runtime = detectVoiceRuntime(win, nav);
  const userAgent = String(nav?.userAgent || "");
  const isTouchMac =
    /Macintosh/i.test(userAgent) &&
    Number(nav?.maxTouchPoints || 0) > 1;
  const isIOS = /iPhone|iPad|iPod/i.test(userAgent) || isTouchMac;
  const isCriOS = isIOS && /CriOS/i.test(userAgent);
  const standalone = Boolean(
    nav?.standalone === true ||
      win?.matchMedia?.("(display-mode: standalone)")?.matches
  );
  const isSafari = Boolean(
    isIOS &&
      /AppleWebKit/i.test(userAgent) &&
      !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(userAgent)
  );

  return {
    ...runtime,
    isIOS,
    isCriOS,
    isSafari,
    standalone,
  };
};

const permissionDeniedMessage = (profile) => {
  if (profile.isCriOS) {
    return "MICROPHONE ACCESS DENIED. OPEN IPHONE SETTINGS, CHOOSE CHROME, ENABLE MICROPHONE, THEN RETURN AND TAP TALK.";
  }
  if (profile.isIOS && profile.standalone) {
    return "MICROPHONE ACCESS DENIED. ENABLE MICROPHONE ACCESS FOR CORTEX OR SAFARI IN IPHONE SETTINGS, THEN REOPEN THE HOME SCREEN APP.";
  }
  if (profile.isSafari) {
    return "MICROPHONE ACCESS DENIED. IN SAFARI, OPEN PAGE SETTINGS FOR THIS SITE AND SET MICROPHONE TO ALLOW; IF NEEDED, ENABLE SAFARI MICROPHONE ACCESS IN IPHONE SETTINGS.";
  }
  return VOICE_INPUT_STATUS_MESSAGES[VOICE_INPUT_PHASES.PERMISSION_DENIED];
};

const recognitionUnavailableMessage = (profile) => {
  if (profile.isCriOS) {
    return "SPEECH RECOGNITION IS UNAVAILABLE IN CHROME ON THIS IPHONE. OPEN CORTEX IN SAFARI OR TYPE YOUR EDEN REQUEST.";
  }
  if (profile.isIOS && profile.standalone) {
    return "SPEECH RECOGNITION IS UNAVAILABLE IN THIS INSTALLED CORTEX RUNTIME. OPEN CORTEX IN SAFARI OR TYPE YOUR EDEN REQUEST.";
  }
  if (profile.isSafari) {
    return "SPEECH RECOGNITION IS UNAVAILABLE IN SAFARI. ENABLE SIRI OR DICTATION IN IPHONE SETTINGS, THEN TAP TALK AGAIN; YOU CAN STILL TYPE YOUR EDEN REQUEST.";
  }
  return VOICE_INPUT_STATUS_MESSAGES[
    VOICE_INPUT_PHASES.RECOGNITION_UNAVAILABLE
  ];
};

const recognitionStartTimeoutMessage = (profile) => {
  if (profile.isCriOS) {
    return "SPEECH RECOGNITION DID NOT START IN CHROME ON THIS IPHONE. COMPLETE ANY OPEN PERMISSION PROMPT, OR OPEN CORTEX IN SAFARI; YOU CAN STILL TYPE YOUR EDEN REQUEST.";
  }
  if (profile.isIOS) {
    return "SPEECH RECOGNITION DID NOT START. COMPLETE ANY OPEN PERMISSION PROMPT AND CONFIRM SIRI OR DICTATION IS ENABLED IN IPHONE SETTINGS, THEN TAP TALK AGAIN; YOU CAN STILL TYPE.";
  }
  return "SPEECH RECOGNITION DID NOT START. COMPLETE ANY OPEN BROWSER PERMISSION PROMPT, THEN TAP TALK TO TRY AGAIN; YOU CAN STILL TYPE.";
};

const pageInterruptionMessage = () =>
  "VOICE INPUT STOPPED WHEN CORTEX LEFT THE FOREGROUND. RETURN TO CORTEX AND TAP TALK TO TRY AGAIN; YOU CAN STILL TYPE.";

const speechWithoutTranscriptMessage = () =>
  "SPEECH WAS HEARD, BUT NO TRANSCRIPT WAS RETURNED. TAP TALK TO TRY AGAIN; YOU CAN STILL TYPE.";

const transcriptionTimeoutMessage = () =>
  "VOICE INPUT TIMED OUT BEFORE TRANSCRIPTION COMPLETED. TAP TALK TO TRY AGAIN; YOU CAN STILL TYPE.";

const audioCaptureMessage = (profile, errorCode) => {
  if (errorCode === "microphone_not_found") {
    return "NO MICROPHONE WAS FOUND. CONNECT OR ENABLE A MICROPHONE, THEN TAP TALK TO TRY AGAIN.";
  }
  if (profile.isIOS) {
    return "IPHONE COULD NOT START AUDIO CAPTURE. CLOSE OTHER APPS USING THE MICROPHONE, CHECK MICROPHONE ACCESS, THEN TAP TALK TO TRY AGAIN.";
  }
  return VOICE_INPUT_STATUS_MESSAGES[
    VOICE_INPUT_PHASES.AUDIO_CAPTURE_FAILURE
  ];
};

const normalizeMediaFailure = (error, profile) => {
  const name = String(error?.name || error?.code || "unknown");
  const normalizedName = name.toLowerCase();

  if (
    normalizedName === "notallowederror" ||
    normalizedName === "permissiondeniederror" ||
    normalizedName === "securityerror"
  ) {
    return {
      phase: VOICE_INPUT_PHASES.PERMISSION_DENIED,
      code: "microphone_permission_denied",
      message: permissionDeniedMessage(profile),
    };
  }

  if (
    normalizedName === "notfounderror" ||
    normalizedName === "devicesnotfounderror" ||
    normalizedName === "overconstrainederror"
  ) {
    return {
      phase: VOICE_INPUT_PHASES.AUDIO_CAPTURE_FAILURE,
      code: "microphone_not_found",
      message: audioCaptureMessage(profile, "microphone_not_found"),
    };
  }

  if (normalizedName === "typeerror") {
    return {
      phase: VOICE_INPUT_PHASES.RECOGNITION_UNAVAILABLE,
      code: "microphone_runtime_unavailable",
      message:
        "MICROPHONE CAPTURE IS UNAVAILABLE IN THIS RUNTIME. USE A SECURE CORTEX PAGE OR CONTINUE BY TYPING.",
    };
  }

  return {
    phase: VOICE_INPUT_PHASES.AUDIO_CAPTURE_FAILURE,
    code: "audio_capture_failed",
    message: audioCaptureMessage(profile, "audio_capture_failed"),
  };
};

const normalizeRecognitionStartFailure = (error, profile) => {
  const browserErrorCode =
    String(error?.name || error?.code || "start_failed")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "_")
    .slice(0, 64) || "start_failed";

  if (
    browserErrorCode === "notallowederror" ||
    browserErrorCode === "securityerror"
  ) {
    return {
      phase: VOICE_INPUT_PHASES.PERMISSION_DENIED,
      code: "speech_recognition_permission_denied",
      message: permissionDeniedMessage(profile),
      browserErrorCode,
    };
  }

  if (browserErrorCode === "invalidstateerror") {
    return {
      phase: VOICE_INPUT_PHASES.ERROR,
      code: "recognition_invalid_state",
      message:
        "SPEECH RECOGNITION COULD NOT START A NEW SESSION. TAP TALK TO TRY AGAIN; YOU CAN STILL TYPE.",
      browserErrorCode,
    };
  }

  return {
    phase: VOICE_INPUT_PHASES.RECOGNITION_UNAVAILABLE,
    code:
      browserErrorCode === "notsupportederror"
        ? "recognition_not_supported"
        : "recognition_start_failed",
    message: recognitionUnavailableMessage(profile),
    browserErrorCode,
  };
};

const normalizeRecognitionFailure = (rawError, profile, manualStop) => {
  const classification = classifyRecognitionError(rawError, {
    runtime: profile,
    manualStop,
  });
  if (!classification) return null;

  switch (classification.code) {
    case "microphone_blocked":
      return {
        phase: VOICE_INPUT_PHASES.PERMISSION_DENIED,
        code: "microphone_permission_denied",
        message: permissionDeniedMessage(profile),
      };
    case "audio_capture_failed":
      return {
        phase: VOICE_INPUT_PHASES.AUDIO_CAPTURE_FAILURE,
        code: classification.code,
        message: audioCaptureMessage(profile, classification.code),
      };
    case "no_speech":
      return {
        phase: VOICE_INPUT_PHASES.NO_SPEECH,
        code: classification.code,
        message: VOICE_INPUT_STATUS_MESSAGES[VOICE_INPUT_PHASES.NO_SPEECH],
      };
    case "recognition_service_unavailable":
    case "language_not_supported":
      return {
        phase: VOICE_INPUT_PHASES.RECOGNITION_UNAVAILABLE,
        code: classification.code,
        message: recognitionUnavailableMessage(profile),
      };
    case "recognition_network_failed":
      return {
        phase: VOICE_INPUT_PHASES.RECOGNITION_UNAVAILABLE,
        code: classification.code,
        message:
          "VOICE RECOGNITION COULD NOT CONNECT. CHECK YOUR CONNECTION, TAP TALK TO TRY AGAIN, OR CONTINUE BY TYPING.",
      };
    default:
      return {
        phase: VOICE_INPUT_PHASES.ERROR,
        code: classification.code || "voice_input_failed",
        message:
          VOICE_INPUT_STATUS_MESSAGES[VOICE_INPUT_PHASES.ERROR],
      };
  }
};

const readRecognitionResults = (event) => {
  const results = Array.from(event?.results || []);
  const resultIndex = Number.isInteger(event?.resultIndex)
    ? Math.max(0, event.resultIndex)
    : 0;
  let interim = "";
  let final = "";

  results.slice(resultIndex).forEach((result) => {
    const text = normalizeTranscript(result?.[0]?.transcript || "");
    if (!text) return;
    if (result?.isFinal) {
      final = appendVoiceTranscript(final, text);
    } else {
      interim = appendVoiceTranscript(interim, text);
    }
  });

  return {
    interim,
    final,
    resultCount: Math.max(0, results.length - resultIndex),
  };
};

export function createVoiceInputSessionController({
  win = typeof window !== "undefined" ? window : undefined,
  nav = typeof navigator !== "undefined" ? navigator : undefined,
  callbacks = {},
  permissionTimeoutMs = 20000,
  startTimeoutMs = 20000,
  listeningTimeoutMs = 12000,
  utteranceTimeoutMs = 30000,
  speechEndFallbackMs = 500,
} = {}) {
  const setTimer = (callback, delay) =>
    Number(delay) > 0
      ? win && typeof win.setTimeout === "function"
        ? win.setTimeout(callback, Number(delay))
        : setTimeout(callback, Number(delay))
      : null;
  const clearTimer = (timer) => {
    if (timer == null) return;
    if (win && typeof win.clearTimeout === "function") {
      win.clearTimeout(timer);
    } else {
      clearTimeout(timer);
    }
  };

  let snapshot = INITIAL_VOICE_INPUT_SNAPSHOT;
  let activeSession = null;
  let sessionSequence = 0;
  let destroyed = false;
  let currentCallbacks = { ...callbacks };

  const emitDiagnostic = (event, detail = {}) => {
    safeCall(currentCallbacks.onDiagnostic, event, Object.freeze({ ...detail }));
  };

  const transition = (
    phase,
    {
      message = VOICE_INPUT_STATUS_MESSAGES[phase],
      errorCode = null,
      sessionId = activeSession?.id || null,
      transcriptChars = 0,
      notify = true,
    } = {}
  ) => {
    snapshot = Object.freeze({
      phase,
      message,
      errorCode,
      sessionId,
      active: ACTIVE_PHASES.has(phase),
      isActive: ACTIVE_PHASES.has(phase),
      isListening: phase === VOICE_INPUT_PHASES.LISTENING,
      transcriptChars,
    });
    if (notify && !destroyed) {
      safeCall(currentCallbacks.onStateChange, snapshot);
    }
    return snapshot;
  };

  const clearSessionTimers = (session) => {
    clearTimer(session?.permissionTimer);
    clearTimer(session?.startTimer);
    clearTimer(session?.listeningTimer);
    clearTimer(session?.utteranceTimer);
    clearTimer(session?.speechEndTimer);
    clearTimer(session?.completionTimer);
    session.permissionTimer = null;
    session.startTimer = null;
    session.listeningTimer = null;
    session.utteranceTimer = null;
    session.speechEndTimer = null;
    session.completionTimer = null;
  };

  const unregisterRecognitionHandlers = (session) => {
    const bindings = Array.isArray(session?.handlerBindings)
      ? session.handlerBindings.splice(0)
      : [];
    bindings.forEach(({ eventName, handler, propertyMode }) => {
      try {
        if (propertyMode) {
          const property = `on${eventName}`;
          if (session.recognition?.[property] === handler) {
            session.recognition[property] = null;
          }
        } else {
          session.recognition?.removeEventListener?.(eventName, handler);
        }
      } catch {}
    });
  };

  const unregisterLifecycleHandlers = (session) => {
    const bindings = Array.isArray(session?.lifecycleBindings)
      ? session.lifecycleBindings.splice(0)
      : [];
    bindings.forEach(({ target, eventName, handler }) => {
      try {
        target?.removeEventListener?.(eventName, handler);
      } catch {}
    });
  };

  const bindLifecycleHandler = (session, target, eventName, handler) => {
    if (typeof target?.addEventListener !== "function") return;
    target.addEventListener(eventName, handler);
    session.lifecycleBindings.push({ target, eventName, handler });
  };

  const releaseStream = (session) => {
    if (!session?.stream) return 0;
    const trackCount = stopMediaStream(session.stream);
    session.stream = null;
    session.streamAcquired = false;
    emitDiagnostic("media_stream_released", {
      sessionId: session.id,
      trackCount,
    });
    return trackCount;
  };

  const emitRecognitionEnd = (
    session,
    { browserEventObserved, termination }
  ) => {
    if (!session?.recognitionStartRequested || session.endDiagnosticEmitted) {
      return;
    }
    session.endDiagnosticEmitted = true;
    emitDiagnostic(VOICE_INPUT_DIAGNOSTIC_EVENTS.RECOGNITION_END, {
      sessionId: session.id,
      browserEventObserved: Boolean(browserEventObserved),
      termination,
      hadSpeechStart: session.speechStarted,
      hadInterim: Boolean(session.interimTranscript),
      hadFinal: Boolean(session.finalTranscript),
      errorCode: session.errorCode,
      manualStop: session.manualStop,
    });
  };

  const finalizeSession = (
    session,
    { abortRecognition = false, stopRecognition = false } = {}
  ) => {
    if (!session || session.finalized) return;
    session.finalized = true;
    clearSessionTimers(session);
    emitRecognitionEnd(session, {
      browserEventObserved: session.recognitionEnded,
      termination: abortRecognition
        ? "abort"
        : stopRecognition
        ? "stop"
        : "cleanup",
    });
    unregisterLifecycleHandlers(session);
    unregisterRecognitionHandlers(session);
    const recognition = session.recognition;
    session.recognition = null;
    if (abortRecognition) {
      try {
        recognition?.abort?.();
      } catch {}
    } else if (stopRecognition) {
      try {
        recognition?.stop?.();
      } catch {}
    }
    releaseStream(session);
    safeCall(currentCallbacks.onInterimTranscript, "", {
      sessionId: session.id,
    });
    if (session.listeningNotified) {
      session.listeningNotified = false;
      safeCall(currentCallbacks.onListeningStop, {
        sessionId: session.id,
      });
    }
    session.interimTranscript = "";
    session.finalTranscript = "";
    if (activeSession === session) {
      activeSession = null;
    }
  };

  const failSession = (
    session,
    { phase, code, message, abortRecognition = true }
  ) => {
    if (
      !session ||
      session.finalized ||
      activeSession !== session ||
      session.cancelled
    ) {
      return;
    }
    session.errorCode = code;
    session.terminalPhase = phase;
    clearSessionTimers(session);
    transition(phase, {
      message,
      errorCode: code,
      sessionId: session.id,
    });
    safeCall(currentCallbacks.onError, {
      phase,
      errorCode: code,
      sessionId: session.id,
    });
    finalizeSession(session, { abortRecognition });
  };

  const bindRecognitionHandler = (session, eventName, handler) => {
    const recognition = session.recognition;
    if (typeof recognition?.addEventListener === "function") {
      recognition.addEventListener(eventName, handler);
      session.handlerBindings.push({
        eventName,
        handler,
        propertyMode: false,
      });
      return;
    }
    const property = `on${eventName}`;
    recognition[property] = handler;
    session.handlerBindings.push({
      eventName,
      handler,
      propertyMode: true,
    });
  };

  const isCurrentSession = (session) =>
    !destroyed &&
    activeSession === session &&
    !session.finalized &&
    !session.cancelled;

  const beginListeningIfReady = (session) => {
    if (
      !isCurrentSession(session) ||
      session.captureStarted ||
      session.listeningConfirmed ||
      !session.recognitionStarted ||
      !session.mediaValidated
    ) {
      return;
    }

    session.listeningConfirmed = true;
    clearTimer(session.startTimer);
    session.startTimer = null;
    transition(VOICE_INPUT_PHASES.LISTENING, {
      sessionId: session.id,
    });
    session.listeningNotified = true;
    safeCall(currentCallbacks.onListeningStart, {
      sessionId: session.id,
    });
    session.listeningTimer = setTimer(() => {
      if (!isCurrentSession(session) || session.captureStarted) return;
      failSession(session, {
        phase: VOICE_INPUT_PHASES.NO_SPEECH,
        code: "no_speech_timeout",
        message: VOICE_INPUT_STATUS_MESSAGES[VOICE_INPUT_PHASES.NO_SPEECH],
      });
    }, listeningTimeoutMs);
  };

  const markSpeechActivity = (session) => {
    if (!isCurrentSession(session) || session.captureStarted) return;
    clearTimer(session.listeningTimer);
    session.listeningTimer = null;
    if (session.utteranceTimer != null) return;
    session.utteranceTimer = setTimer(() => {
      if (!isCurrentSession(session) || session.captureStarted) return;
      failSession(session, {
        phase: VOICE_INPUT_PHASES.ERROR,
        code: "transcription_timeout",
        message: transcriptionTimeoutMessage(),
      });
    }, utteranceTimeoutMs);
  };

  const captureTranscript = (
    session,
    transcript,
    source,
    { recognitionAlreadyEnded = false } = {}
  ) => {
    const cleanTranscript = normalizeTranscript(transcript);
    if (
      !cleanTranscript ||
      !isCurrentSession(session) ||
      session.captureStarted
    ) {
      return;
    }

    session.captureStarted = true;
    clearSessionTimers(session);
    transition(VOICE_INPUT_PHASES.PROCESSING, {
      sessionId: session.id,
      transcriptChars: cleanTranscript.length,
    });
    releaseStream(session);

    if (!recognitionAlreadyEnded) {
      try {
        session.recognition?.stop?.();
      } catch {}
    }

    let callbackResult;
    try {
      callbackResult =
        typeof currentCallbacks.onTranscript === "function"
          ? currentCallbacks.onTranscript(cleanTranscript, {
              source,
              sessionId: session.id,
            })
          : undefined;
    } catch {
      failSession(session, {
        phase: VOICE_INPUT_PHASES.ERROR,
        code: "transcript_callback_failed",
        message: VOICE_INPUT_STATUS_MESSAGES[VOICE_INPUT_PHASES.ERROR],
      });
      return;
    }

    Promise.resolve(callbackResult).then(
      () => {
        if (!isCurrentSession(session) || !session.captureStarted) return;
        // Keep PROCESSING visible for at least one browser task before the
        // captured confirmation. This prevents React 18 batching from
        // collapsing both required UI states into a single render.
        session.completionTimer =
          win && typeof win.setTimeout === "function"
            ? win.setTimeout(() => {
                if (!isCurrentSession(session) || !session.captureStarted) {
                  return;
                }
                transition(VOICE_INPUT_PHASES.CAPTURED, {
                  sessionId: session.id,
                  transcriptChars: cleanTranscript.length,
                });
                finalizeSession(session);
              }, 0)
            : setTimeout(() => {
                if (!isCurrentSession(session) || !session.captureStarted) {
                  return;
                }
                transition(VOICE_INPUT_PHASES.CAPTURED, {
                  sessionId: session.id,
                  transcriptChars: cleanTranscript.length,
                });
                finalizeSession(session);
              }, 0);
      },
      () => {
        failSession(session, {
          phase: VOICE_INPUT_PHASES.ERROR,
          code: "transcript_callback_failed",
          message: VOICE_INPUT_STATUS_MESSAGES[VOICE_INPUT_PHASES.ERROR],
        });
      }
    );
  };

  const registerRecognitionHandlers = (session) => {
    bindRecognitionHandler(session, "start", () => {
      if (!isCurrentSession(session)) return;
      session.recognitionStarted = true;
      emitDiagnostic(VOICE_INPUT_DIAGNOSTIC_EVENTS.RECOGNITION_START, {
        sessionId: session.id,
      });
      beginListeningIfReady(session);
    });

    bindRecognitionHandler(session, "audiostart", () => {
      if (!isCurrentSession(session)) return;
      session.audioStarted = true;
      emitDiagnostic(VOICE_INPUT_DIAGNOSTIC_EVENTS.RECOGNITION_AUDIOSTART, {
        sessionId: session.id,
      });
    });

    bindRecognitionHandler(session, "audioend", () => {
      if (!isCurrentSession(session)) return;
      session.audioEnded = true;
      emitDiagnostic(VOICE_INPUT_DIAGNOSTIC_EVENTS.RECOGNITION_AUDIOEND, {
        sessionId: session.id,
      });
    });

    bindRecognitionHandler(session, "speechstart", () => {
      if (!isCurrentSession(session)) return;
      session.speechStarted = true;
      markSpeechActivity(session);
      emitDiagnostic(VOICE_INPUT_DIAGNOSTIC_EVENTS.RECOGNITION_SPEECHSTART, {
        sessionId: session.id,
      });
    });

    bindRecognitionHandler(session, "speechend", () => {
      if (!isCurrentSession(session) || session.captureStarted) return;
      emitDiagnostic(VOICE_INPUT_DIAGNOSTIC_EVENTS.RECOGNITION_SPEECHEND, {
        sessionId: session.id,
        hadInterim: Boolean(session.interimTranscript),
        hadFinal: Boolean(session.finalTranscript),
      });

      // Give Safari a bounded opportunity to emit its corrected final result
      // or normal end event. If neither arrives, speechend still establishes
      // a command boundary so a second utterance cannot be concatenated.
      if (
        !session.finalTranscript &&
        session.speechEndTimer == null
      ) {
        session.speechEndTimer = setTimer(() => {
          session.speechEndTimer = null;
          if (
            !isCurrentSession(session) ||
            session.captureStarted ||
            !session.interimTranscript ||
            session.finalTranscript
          ) {
            return;
          }
          captureTranscript(
            session,
            session.interimTranscript,
            "speechend_fallback"
          );
        }, speechEndFallbackMs);
      }
    });

    bindRecognitionHandler(session, "result", (event) => {
      if (!isCurrentSession(session) || session.captureStarted) return;
      const result = readRecognitionResults(event);
      emitDiagnostic(VOICE_INPUT_DIAGNOSTIC_EVENTS.RECOGNITION_RESULT, {
        sessionId: session.id,
        resultCount: result.resultCount,
        hasInterim: Boolean(result.interim),
        hasFinal: Boolean(result.final),
        interimChars: result.interim.length,
        finalChars: result.final.length,
      });

      if (result.interim || result.final) {
        markSpeechActivity(session);
      }

      if (result.interim) {
        session.interimTranscript = result.interim;
        safeCall(currentCallbacks.onInterimTranscript, result.interim, {
          sessionId: session.id,
        });
      }

      if (result.final) {
        session.finalTranscript = appendVoiceTranscript(
          session.finalTranscript,
          result.final
        );
        session.interimTranscript = "";
        safeCall(currentCallbacks.onInterimTranscript, "", {
          sessionId: session.id,
        });
        captureTranscript(
          session,
          session.finalTranscript,
          "final_result"
        );
      }
    });

    bindRecognitionHandler(session, "nomatch", () => {
      if (!isCurrentSession(session) || session.captureStarted) return;
      emitDiagnostic(VOICE_INPUT_DIAGNOSTIC_EVENTS.RECOGNITION_NOMATCH, {
        sessionId: session.id,
      });
      failSession(session, {
        phase: VOICE_INPUT_PHASES.ERROR,
        code: "recognition_nomatch",
        message: speechWithoutTranscriptMessage(),
      });
    });

    bindRecognitionHandler(session, "error", (event) => {
      if (
        !isCurrentSession(session) ||
        session.captureStarted ||
        session.cancelled
      ) {
        return;
      }
      const failure = normalizeRecognitionFailure(
        event?.error,
        session.profile,
        session.manualStop
      );
      if (!failure) return;
      emitDiagnostic(VOICE_INPUT_DIAGNOSTIC_EVENTS.RECOGNITION_ERROR, {
        sessionId: session.id,
        normalizedErrorCode: failure.code,
        browserErrorCode: String(event?.error || "unknown"),
      });
      failSession(session, failure);
    });

    bindRecognitionHandler(session, "end", () => {
      if (!isCurrentSession(session)) return;
      session.recognitionEnded = true;
      emitRecognitionEnd(session, {
        browserEventObserved: true,
        termination: "browser_end",
      });

      if (session.captureStarted || session.errorCode) {
        unregisterRecognitionHandlers(session);
        session.recognition = null;
        return;
      }

      if (session.interimTranscript) {
        captureTranscript(
          session,
          session.interimTranscript,
          "interim_fallback",
          { recognitionAlreadyEnded: true }
        );
        return;
      }

      if (!session.recognitionStarted || !session.listeningConfirmed) {
        failSession(session, {
          phase: VOICE_INPUT_PHASES.RECOGNITION_UNAVAILABLE,
          code: "recognition_ended_before_start",
          message: recognitionUnavailableMessage(session.profile),
          abortRecognition: false,
        });
        return;
      }

      if (session.speechStarted) {
        failSession(session, {
          phase: VOICE_INPUT_PHASES.ERROR,
          code: "speech_without_transcript",
          message: speechWithoutTranscriptMessage(),
          abortRecognition: false,
        });
        return;
      }

      failSession(session, {
        phase: VOICE_INPUT_PHASES.NO_SPEECH,
        code: "ended_without_transcript",
        message: VOICE_INPUT_STATUS_MESSAGES[VOICE_INPUT_PHASES.NO_SPEECH],
        abortRecognition: false,
      });
    });
  };

  const registerLifecycleHandlers = (session) => {
    const interrupt = (reason) => {
      if (
        !isCurrentSession(session) ||
        session.captureStarted ||
        session.manualStop
      ) {
        return;
      }
      session.pageInterrupted = true;
      emitDiagnostic(VOICE_INPUT_DIAGNOSTIC_EVENTS.PAGE_INTERRUPTION, {
        sessionId: session.id,
        reason,
        phase: snapshot.phase,
      });
      emitDiagnostic(VOICE_INPUT_DIAGNOSTIC_EVENTS.RECOGNITION_ERROR, {
        sessionId: session.id,
        normalizedErrorCode: "page_interrupted",
        browserErrorCode: reason,
      });
      failSession(session, {
        phase: VOICE_INPUT_PHASES.ERROR,
        code: "page_interrupted",
        message: pageInterruptionMessage(),
      });
    };

    bindLifecycleHandler(
      session,
      win?.document,
      "visibilitychange",
      () => {
        if (
          win?.document?.hidden === true ||
          win?.document?.visibilityState === "hidden"
        ) {
          interrupt("visibility_hidden");
        }
      }
    );
    bindLifecycleHandler(session, win, "pagehide", () => {
      interrupt("pagehide");
    });
  };

  const start = () => {
    emitDiagnostic(VOICE_INPUT_DIAGNOSTIC_EVENTS.TALK_GESTURE_RECEIVED, {
      accepted: !destroyed && !activeSession,
      currentPhase: snapshot.phase,
    });
    if (destroyed) return false;

    if (activeSession && !activeSession.finalized) {
      if (
        activeSession.terminalPhase ||
        snapshot.phase === VOICE_INPUT_PHASES.CAPTURED
      ) {
        finalizeSession(activeSession, { abortRecognition: true });
      } else {
        emitDiagnostic("duplicate_talk_ignored", {
          sessionId: activeSession.id,
          currentPhase: snapshot.phase,
        });
        return false;
      }
    }

    const profile = detectRuntimeProfile(win, nav);
    const SpeechRecognition =
      win?.SpeechRecognition || win?.webkitSpeechRecognition;
    const microphoneApiSupported = Boolean(
      nav?.mediaDevices &&
        typeof nav.mediaDevices.getUserMedia === "function"
    );
    const secureContext = win?.isSecureContext !== false;

    emitDiagnostic(
      VOICE_INPUT_DIAGNOSTIC_EVENTS.RUNTIME_CAPABILITY_RESULT,
      {
        speechRecognitionSupported: Boolean(SpeechRecognition),
        microphoneApiSupported,
        secureContext,
        shellKind: profile.shellKind,
        standalone: profile.standalone,
        isIOS: profile.isIOS,
        isCriOS: profile.isCriOS,
      }
    );

    if (!SpeechRecognition) {
      const message = recognitionUnavailableMessage(profile);
      transition(VOICE_INPUT_PHASES.RECOGNITION_UNAVAILABLE, {
        message,
        errorCode: "speech_recognition_unavailable",
        sessionId: null,
      });
      safeCall(currentCallbacks.onError, {
        phase: VOICE_INPUT_PHASES.RECOGNITION_UNAVAILABLE,
        errorCode: "speech_recognition_unavailable",
        sessionId: null,
      });
      return false;
    }

    if (!microphoneApiSupported || !secureContext) {
      const phase = secureContext
        ? VOICE_INPUT_PHASES.AUDIO_CAPTURE_FAILURE
        : VOICE_INPUT_PHASES.RECOGNITION_UNAVAILABLE;
      const errorCode = secureContext
        ? "microphone_api_unavailable"
        : "insecure_context";
      const message = secureContext
        ? audioCaptureMessage(profile, errorCode)
        : "VOICE INPUT REQUIRES A SECURE CORTEX PAGE. CONTINUE BY TYPING IN THIS RUNTIME.";
      transition(phase, {
        message,
        errorCode,
        sessionId: null,
      });
      safeCall(currentCallbacks.onError, {
        phase,
        errorCode,
        sessionId: null,
      });
      return false;
    }

    let recognition;
    try {
      recognition = new SpeechRecognition();
    } catch {
      const code = "recognition_construction_failed";
      emitDiagnostic(VOICE_INPUT_DIAGNOSTIC_EVENTS.RECOGNITION_ERROR, {
        sessionId: null,
        normalizedErrorCode: code,
        browserErrorCode: "construction_failed",
      });
      transition(VOICE_INPUT_PHASES.RECOGNITION_UNAVAILABLE, {
        message: recognitionUnavailableMessage(profile),
        errorCode: code,
        sessionId: null,
      });
      safeCall(currentCallbacks.onError, {
        phase: VOICE_INPUT_PHASES.RECOGNITION_UNAVAILABLE,
        errorCode: code,
        sessionId: null,
      });
      return false;
    }

    const session = {
      id: sessionSequence + 1,
      profile,
      recognition,
      stream: null,
      handlerBindings: [],
      lifecycleBindings: [],
      recognitionStartRequested: false,
      recognitionStarted: false,
      recognitionEnded: false,
      endDiagnosticEmitted: false,
      streamAcquired: false,
      mediaValidated: false,
      listeningConfirmed: false,
      listeningNotified: false,
      speechStarted: false,
      audioStarted: false,
      audioEnded: false,
      captureStarted: false,
      manualStop: false,
      cancelled: false,
      finalized: false,
      errorCode: null,
      terminalPhase: null,
      pageInterrupted: false,
      interimTranscript: "",
      finalTranscript: "",
      permissionTimer: null,
      startTimer: null,
      listeningTimer: null,
      utteranceTimer: null,
      speechEndTimer: null,
      completionTimer: null,
    };
    sessionSequence = session.id;
    activeSession = session;

    recognition.lang = "en-GB";
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    registerRecognitionHandlers(session);
    registerLifecycleHandlers(session);

    transition(VOICE_INPUT_PHASES.REQUESTING_PERMISSION, {
      sessionId: session.id,
    });
    let mediaRequest;
    emitDiagnostic(
      VOICE_INPUT_DIAGNOSTIC_EVENTS.MICROPHONE_PERMISSION_REQUESTED,
      { sessionId: session.id }
    );
    try {
      // Deliberately invoked directly in start(): mobile browsers can require
      // microphone activation in the original TALK user-gesture call stack.
      mediaRequest = nav.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      const failure = normalizeMediaFailure(error, profile);
      emitDiagnostic(VOICE_INPUT_DIAGNOSTIC_EVENTS.RECOGNITION_ERROR, {
        sessionId: session.id,
        normalizedErrorCode: failure.code,
        browserErrorCode: String(error?.name || "get_user_media_failed"),
      });
      failSession(session, failure);
      return false;
    }

    session.permissionTimer = setTimer(() => {
      if (!isCurrentSession(session) || session.mediaValidated) return;
      const failure = {
        phase: VOICE_INPUT_PHASES.AUDIO_CAPTURE_FAILURE,
        code: "microphone_permission_timeout",
        message:
          "MICROPHONE ACCESS DID NOT COMPLETE. CHECK FOR A BROWSER PERMISSION PROMPT, THEN TAP TALK TO TRY AGAIN.",
      };
      emitDiagnostic(VOICE_INPUT_DIAGNOSTIC_EVENTS.RECOGNITION_ERROR, {
        sessionId: session.id,
        normalizedErrorCode: failure.code,
        browserErrorCode: "permission_timeout",
      });
      failSession(session, failure);
    }, permissionTimeoutMs);

    const requestRecognitionStart = () => {
      if (!isCurrentSession(session) || !session.mediaValidated) return false;
      session.recognitionStartRequested = true;
      emitDiagnostic(
        VOICE_INPUT_DIAGNOSTIC_EVENTS.RECOGNITION_START_REQUESTED,
        {
          sessionId: session.id,
          afterMicrophonePermission: true,
        }
      );
      session.startTimer = setTimer(() => {
        if (
          !isCurrentSession(session) ||
          session.captureStarted ||
          session.recognitionStarted
        ) {
          return;
        }
        failSession(session, {
          phase: VOICE_INPUT_PHASES.ERROR,
          code: "recognition_start_timeout",
          message: recognitionStartTimeoutMessage(profile),
        });
      }, startTimeoutMs);

      try {
        // The permission request itself was made in the TALK gesture. Start
        // recognition only after that request returns a live audio track, and
        // after releasing the preflight stream, so Safari does not have two
        // independent microphone clients competing at once.
        recognition.start();
        return true;
      } catch (error) {
        const failure = normalizeRecognitionStartFailure(error, profile);
        emitDiagnostic(VOICE_INPUT_DIAGNOSTIC_EVENTS.RECOGNITION_ERROR, {
          sessionId: session.id,
          normalizedErrorCode: failure.code,
          browserErrorCode: failure.browserErrorCode,
        });
        failSession(session, failure);
        return false;
      }
    };

    Promise.resolve(mediaRequest).then(
      (stream) => {
        if (!isCurrentSession(session) || session.captureStarted) {
          const trackCount = stopMediaStream(stream);
          emitDiagnostic("stale_media_stream_discarded", {
            sessionId: session.id,
            trackCount,
          });
          return;
        }
        if (!stream || typeof stream.getTracks !== "function") {
          failSession(session, {
            phase: VOICE_INPUT_PHASES.AUDIO_CAPTURE_FAILURE,
            code: "invalid_media_stream",
            message: audioCaptureMessage(profile, "invalid_media_stream"),
          });
          return;
        }
        clearTimer(session.permissionTimer);
        session.permissionTimer = null;
        const tracks = Array.from(stream.getTracks() || []);
        const audioTracks =
          typeof stream.getAudioTracks === "function"
            ? Array.from(stream.getAudioTracks() || [])
            : tracks.filter((track) => track?.kind === "audio");
        const liveAudioTracks = audioTracks.filter(
          (track) => !track?.readyState || track.readyState === "live"
        );
        if (liveAudioTracks.length === 0) {
          const trackCount = stopMediaStream(stream);
          emitDiagnostic("invalid_media_stream_released", {
            sessionId: session.id,
            trackCount,
          });
          failSession(session, {
            phase: VOICE_INPUT_PHASES.AUDIO_CAPTURE_FAILURE,
            code: "no_live_audio_track",
            message: audioCaptureMessage(profile, "audio_capture_failed"),
          });
          return;
        }
        session.stream = stream;
        session.streamAcquired = true;
        session.mediaValidated = true;
        emitDiagnostic(
          VOICE_INPUT_DIAGNOSTIC_EVENTS.MEDIA_STREAM_ACQUIRED,
          {
            sessionId: session.id,
            trackCount: tracks.length,
            liveAudioTrackCount: liveAudioTracks.length,
          }
        );
        releaseStream(session);
        requestRecognitionStart();
      },
      (error) => {
        if (!isCurrentSession(session) || session.captureStarted) return;
        const failure = normalizeMediaFailure(error, profile);
        emitDiagnostic(VOICE_INPUT_DIAGNOSTIC_EVENTS.RECOGNITION_ERROR, {
          sessionId: session.id,
          normalizedErrorCode: failure.code,
          browserErrorCode: String(error?.name || "get_user_media_failed"),
        });
        failSession(session, failure);
      }
    );

    return true;
  };

  const stop = (reason = "user_stop") => {
    emitDiagnostic(VOICE_INPUT_DIAGNOSTIC_EVENTS.USER_STOP, {
      reason: String(reason || "user_stop"),
      hadActiveSession: Boolean(activeSession),
      sessionId: activeSession?.id || null,
    });
    if (activeSession) {
      const session = activeSession;
      session.manualStop = true;
      session.cancelled = true;
      finalizeSession(session, { abortRecognition: true });
    }
    transition(VOICE_INPUT_PHASES.IDLE, {
      sessionId: null,
    });
  };

  const cleanup = () => {
    if (destroyed) return;
    emitDiagnostic(VOICE_INPUT_DIAGNOSTIC_EVENTS.COMPONENT_CLEANUP, {
      hadActiveSession: Boolean(activeSession),
      sessionId: activeSession?.id || null,
    });
    if (activeSession) {
      activeSession.manualStop = true;
      activeSession.cancelled = true;
      finalizeSession(activeSession, { abortRecognition: true });
    }
    destroyed = true;
    transition(VOICE_INPUT_PHASES.IDLE, {
      sessionId: null,
      notify: false,
    });
  };

  const updateCallbacks = (nextCallbacks = {}) => {
    currentCallbacks = { ...nextCallbacks };
  };

  return Object.freeze({
    start,
    stop,
    cancel: stop,
    cleanup,
    updateCallbacks,
    getSnapshot: () => snapshot,
    isActive: () => Boolean(activeSession && !activeSession.finalized),
    isDestroyed: () => destroyed,
  });
}
